/* eslint-disable radix */
import React, { useState } from "react";
import { Alert, message, Row, Col } from "antd";
import moment from "moment";
import styled from "styled-components";
// hooks
import { useQuery, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import useLocale from "hooks/useLocale";

// graphql
import GET_PROGRAMS from "graphql/queries/GetPrograms";
import UPDATE_TRIAL_PACKAGE from "graphql/mutations/UpdateTrialPackage";

// components
import { Program } from "types";
import { H4 } from "common/text";

import {
  ProgramSelectorContainer,
  ProgramInfoContainer,
  ProgramDescription,
  Price,
  CurrencySign,
  PerUnitTime,
  SubscriptionStatusTag,
} from "common/program_selector";
import Button from "common/button";
import getCurrencySign from "utils/currency";
import screenSize from "utils/screenSize";
import outputManuallySetPrice from "utils/outputManuallySetPrice";
import resizeOverflowingPrice from "utils/resizeOverflowingPrice";
import CancelTrialPackageModal from "../CancelTrialPackageModal";
import ReactivateTrialPackageModal from "./ReactivateTrialPackageModal";
import { ProgramOption } from "../styles";

type TProps = {
  id: number;
  activeTrialStatus: string;
  priceId: string;
  currency?: string | undefined;
  endDate: Date;
};

const CancelTrialPackage = ({
  id,
  activeTrialStatus,
  priceId,
  currency,
  endDate,
}: TProps) => {
  const { t } = useTranslation("my_credits");
  const StyledSecondaryButton = styled(Button)`
    background-color: #d3e8ff;
    color: #1070b6;
    font-weight: bold;
    text-align: center;

    :hover,
    :active,
    :focus {
      background-color: #d3e8ff;
      filter: brightness(90%);
    }
  `;
  // hooks
  const [cancelTrialModalVisble, setCancelTrialModalVisble] = useState(false);
  const [reactivateTrialModalVisble, setReactivateTrialModalVisble] =
    useState(false);

  // queries
  const language = useLocale();

  const { data, loading } = useQuery(GET_PROGRAMS, {
    variables: {
      currency,
      language,
      studyLanguageId: null,
    },
  });

  // mutations
  const [cancelTrialPackageMutation] = useMutation(UPDATE_TRIAL_PACKAGE);

  const handleClick = async (trialId: number | undefined, status: string) => {
    /* trial status: enum status:{trialing: 0, canceled: 1, paid: 2} in flexi/app/models/trial_package.rb */
    if (!trialId && !status) return;

    try {
      await cancelTrialPackageMutation({
        variables: {
          input: {
            id: trialId,
            status,
          },
        },
      });
      if (status === "canceled") {
        message.info(t("Successfully canceled!"));
      }
      if (status === "trialing") {
        message.info(t("Successfully reactivated!"));
      }
      setCancelTrialModalVisble(false);
      setReactivateTrialModalVisble(false);
    } catch (e) {
      throw new Error(t("Invalid input"));
    }
  };
  if (loading) return <Alert type="info" message={t("Loading...")} />;

  const renderCancelButton = (
    <div>
      <p style={{ color: "#282828", fontSize: "12px" }}>
        {activeTrialStatus === "trialing" &&
          t(
            "You will make an one-off payment and recieve your credits on {{ date }}.",
            {
              date: moment(endDate).format("LL"),
            }
          )}
        {activeTrialStatus === "canceled" && t("Your package was canceled")}
        {activeTrialStatus === "paid" &&
          t("Your package was paid on {{ date }}.", {
            date: moment(endDate).format("LL"),
          })}
      </p>
      {activeTrialStatus === "trialing" && (
        <StyledSecondaryButton onClick={() => setCancelTrialModalVisble(true)}>
          {t("CANCEL")}
        </StyledSecondaryButton>
      )}
      {activeTrialStatus === "canceled" && (
        <StyledSecondaryButton
          onClick={() => setReactivateTrialModalVisble(true)}
        >
          {t("REACTIVATE")}
        </StyledSecondaryButton>
      )}
    </div>
  );
  const activeProgram: Program | undefined = data?.plans.find(
    (program: any) => program.id === priceId
  );

  // if there's no active subscription/program
  if (!activeProgram) {
    return (
      <div style={{ marginTop: 18 }}>
        <Alert
          type="warning"
          message={t(
            "You didn't subscribe to any program yet. Subscribe by selecting one above."
          )}
        />
      </div>
    );
  }

  const ActiveTag = (
    <SubscriptionStatusTag>{t("ACTIVE")}</SubscriptionStatusTag>
  );
  const PausedTag = (
    <SubscriptionStatusTag gray>{t("CANCELED")}</SubscriptionStatusTag>
  );
  const PaidTag = (
    <SubscriptionStatusTag gray style={{ width: "auto" }}>
      {t("PAID")}
    </SubscriptionStatusTag>
  );
  const renderSubscriptionStatusTag = () => {
    if (activeTrialStatus === "trialing") {
      return ActiveTag;
    }
    if (activeTrialStatus === "canceled") {
      return PausedTag;
    }
    return PaidTag;
  };

  return (
    <ProgramSelectorContainer value={activeProgram.id}>
      <H4>MY ONE-OFF PURCHASE</H4>
      <Row>
        <Col md={12} sm={24}>
          <ProgramOption
            key={activeProgram.id}
            value={activeProgram.id}
            style={
              activeTrialStatus === "trialing"
                ? { backgroundColor: "#D3E8FF" }
                : { backgroundColor: "#d3d3d3" }
            }
            disabled={activeTrialStatus !== "trialing"}
          >
            <ProgramInfoContainer gutter={[10, 10]}>
              <ProgramDescription sm={16} xs={12}>
                <div style={{ whiteSpace: "normal" }}>{activeProgram.name}</div>
                {screenSize() === "mobile" && (
                  <div style={{ flexBasis: "100%" }} />
                )}
                {renderSubscriptionStatusTag()}
              </ProgramDescription>
              <Price
                sm={8}
                xs={12}
                style={{
                  fontSize: resizeOverflowingPrice(
                    outputManuallySetPrice(activeProgram.price)
                  ),
                  color: activeTrialStatus === "trialing" ? "#1070b6" : "grey",
                }}
              >
                <CurrencySign>
                  <span // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: getCurrencySign(currency?.toString() || ""),
                    }}
                  />
                </CurrencySign>
                {outputManuallySetPrice(activeProgram.price)}
                <PerUnitTime>{t("/month")}</PerUnitTime>
              </Price>
            </ProgramInfoContainer>
          </ProgramOption>
        </Col>
      </Row>
      {renderCancelButton}
      <CancelTrialPackageModal
        visible={cancelTrialModalVisble}
        isTrialPackageModal
        onCancel={() => setCancelTrialModalVisble(false)}
        onConfirm={() => handleClick(id, "canceled")}
        loading={false}
      />
      <ReactivateTrialPackageModal
        visible={reactivateTrialModalVisble}
        endDate={endDate}
        onCancel={() => setReactivateTrialModalVisble(false)}
        onConfirm={() => handleClick(id, "trialing")}
        loading={false}
      />
    </ProgramSelectorContainer>
  );
};

export default CancelTrialPackage;
